import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  muted: "#6b7280",
  text: "#1f2933",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const DialogHeader = ({ icon: Icon, title }) => (
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      fontWeight: 700,
      color: ui.text,
      pb: 1.5,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#e6f6f9",
        color: ui.primary,
      }}
    >
      <Icon fontSize="small" />
    </Box>
    {title}
  </DialogTitle>
);

const TaskFormDialog = ({
  openForm,
  handleCloseForm,
  isEditing,
  formData,
  errors,
  submitError,
  fetchUserError,
  loading,
  currentUser,
  isMobile,
  handleChange,
  handleDateChange,
  handleSubmit,
}) => {
  return (
    <Dialog
      open={openForm}
      onClose={handleCloseForm}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: { xs: 0, sm: "16px" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          maxWidth: { xs: "100%", sm: "600px" },
        },
      }}
    >
      <DialogHeader
        icon={AssignmentOutlinedIcon}
        title={isEditing ? "Edit Jira Task" : "Add New Jira Task"}
      />
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        {submitError && (
          <Alert
            severity="error"
            variant="filled"
            sx={{ mb: 2.5, borderRadius: "10px" }}
          >
            {submitError}
          </Alert>
        )}
        {fetchUserError && (
          <Alert
            severity="warning"
            variant="filled"
            sx={{ mb: 2.5, borderRadius: "10px" }}
          >
            {fetchUserError}
          </Alert>
        )}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2.5,
            mt: 0.5,
          }}
        >
          <TextField
            name="taskId"
            label="Task ID (e.g., JIRA-123)"
            value={formData.taskId}
            onChange={handleChange}
            error={!!errors.taskId}
            helperText={errors.taskId}
            fullWidth
            required
            autoFocus
            sx={fieldSx}
            disabled={isEditing}
          />
          <TextField
            name="storyPoints"
            label="Story Points"
            value={formData.storyPoints}
            onChange={handleChange}
            error={!!errors.storyPoints}
            helperText={errors.storyPoints}
            type="number"
            fullWidth
            required
            sx={fieldSx}
          />
          <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
            <TextField
              name="title"
              label="Title"
              value={formData.title}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
              required
              sx={fieldSx}
            />
          </Box>
          <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
            <TextField
              name="link"
              label="Link"
              value={formData.link}
              onChange={handleChange}
              error={!!errors.link}
              helperText={errors.link}
              fullWidth
              required
              type="url"
              sx={fieldSx}
            />
          </Box>
          <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
            <DateTimePicker
              label="Due Date and Time"
              value={formData.dueDate}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={fieldSx}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleCloseForm}
          sx={{ color: ui.muted, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={
            loading ||
            Object.values(errors).some((error) => error) ||
            !currentUser
          }
          startIcon={
            loading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : null
          }
          sx={{ ...primaryButtonSx, py: 1 }}
        >
          {loading ? "Saving..." : isEditing ? "Update Task" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;